type Service = Record<string, any>;

export function serviceActions(service: Service | null): Set<string> {
  const actions = new Set<string>();
  if (!service || ['queued', 'preparing', 'running', 'deploying', 'verifying', 'migrating', 'deleted'].includes(service.status)) return actions;
  actions.add('edit');
  actions.add('compose');
  const deployed = !!service.deployedVersion || !!service.runtime?.length || ['healthy', 'degraded', 'stopped'].includes(service.status);
  if (!deployed && !service.latestDeployment && ['draft', 'ready'].includes(service.status)) actions.add('delete-draft');
  if (!service.serverId) return actions;
  if(!deployed)actions.add('preflight');
  actions.add('domain');
  if (!deployed) actions.add('deploy');
  if (deployed) {
    actions.add('health');
    actions.add('migrate');
    actions.add('remove');
    if (service.status === 'stopped') actions.add('start');
    else {
      actions.add('restart');
      actions.add('stop');
    }
    if (service.deployedVersion && service.deployedVersion !== service.composeVersion) actions.add('apply-compose');
  } else if (service.deploymentStarted) actions.add('remove');
  return actions;
}

export function containerId(container: Service): string {
  return String(container.containerId || container.id || container.ID || '');
}

export function shortContainerId(container: Service): string {
  return containerId(container).replace(/^sha256:/, '').slice(0, 12) || '—';
}

export function runtimeState(container: Service): string {
  const state = String(container.state || container.State || 'unknown').toLowerCase();
  return ({ running: '실행 중', restarting: '재시작 중', exited: '종료됨', stopped: '중지됨', created: '생성됨', paused: '일시 정지', dead: '실행 불가', removing: '제거 중', missing: '컨테이너 없음', unknown: '확인 전' } as Record<string, string>)[state] || state;
}

export function runtimeHealth(container: Service): string {
  const health = String(container.health || container.Health || 'none').toLowerCase();
  return ({ healthy: '정상', unhealthy: '비정상', starting: '초기 점검 중', none: '점검 미설정', unknown: '확인 전' } as Record<string, string>)[health] || health;
}

export function runtimeImage(container: Service): string {
  const image = String(container.image || container.Image || '');
  return image.replace(/(?:@)?sha256:[a-f\d]+/gi, '').replace(/@$/, '') || '이미지 이름 없음';
}

export function probeStatus(status: string): string {
  return ({ healthy: '정상', 'unexpected-status': '응답 코드 불일치', 'connection-or-tls-failed': '연결 / TLS 실패', 'connection-failed': '연결 실패', 'unconfigured-host': '대상 주소 없음', interrupted: '점검 중단' } as Record<string, string>)[status] || status || '확인 전';
}

export function serviceDomainLabel(zone: string, domain: string): string {
  if(domain===zone)return '@';
  return domain.endsWith('.'+zone)?domain.slice(0,-zone.length-1):domain;
}
export function serviceDomainName(zone: string, label: string): string {
  const value=label.trim().toLowerCase();
  if(!zone)throw new Error('Cloudflare Zone을 확인해 주세요.');
  if(value===zone || value.endsWith('.'+zone))throw new Error('Zone을 제외한 서비스 도메인만 입력하세요. 예: health');
  if(value==='@')return zone;
  if(!value || !value.split('.').every(part=>/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(part)))throw new Error('서비스 도메인은 영문·숫자·하이픈으로 입력하세요.');
  return value+'.'+zone;
}

export type ContainerLogLine = { message: string; timestamp?: string; timeLabel?: string };

export function containerLogLines(output: string): ContainerLogLine[] {
  return output ? output.split('\n').map(line => {
    const match = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?(?:Z|[+-]\d{2}:\d{2}))(?:[ \t]|$)/.exec(line);
    if (!match) return { message: line };
    const instant = new Date(match[1]);
    if (!Number.isFinite(instant.getTime())) return { message: line };
    const timeLabel = new Date(instant.getTime() + 9 * 60 * 60 * 1000).toISOString().slice(0, 23).replace('T', ' ');
    return { timestamp: match[1], timeLabel, message: line.slice(match[0].length) };
  }) : [];
}
